import { CARD_FIELD_GROUPS } from 'config/erpProductFields';
import { AttributeDefinition, ProductSummary } from 'types/product';
import { getProductDisplayName } from 'utils/productImage';

export type CardFieldOption = { value: string; label: string };

const specValue = (product: ProductSummary, key: string): string | null => {
  const spec = product.specs?.find((s) => s.key.toLowerCase() === key.toLowerCase());
  return spec?.value?.trim() || null;
};

const firstValue = (...values: Array<string | null | undefined>): string | null => {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return null;
};

export const resolveCardFieldValue = (product: ProductSummary, field?: string): string | null => {
  if (!field || field === 'none') return null;
  if (field.startsWith('spec:')) {
    return specValue(product, field.slice(5));
  }

  switch (field) {
    case 'name':
      return getProductDisplayName(product);
    case 'brand':
      return firstValue(product.brandName, specValue(product, 'marca'));
    case 'sku':
      return firstValue(product.sku, product.id, specValue(product, 'codigo'));
    case 'price':
      return product.price != null ? String(product.price) : null;
    case 'category':
    case 'ramo':
      return firstValue(product.ramoName, product.categoryName, specValue(product, 'ramo'));
    case 'linea':
      return firstValue(product.lineaName, specValue(product, 'linea'));
    case 'clase':
      return firstValue(product.claseName, specValue(product, 'clase'));
    case 'atributo1':
      return firstValue(product.atributo1, specValue(product, 'atributo1'));
    case 'atributo2':
      return firstValue(product.atributo2, specValue(product, 'atributo2'), specValue(product, 'familia'));
    case 'atributo3':
      return firstValue(product.atributo3, specValue(product, 'atributo3'), specValue(product, 'serie'));
    case 'barcode':
      return firstValue(product.barcode, specValue(product, 'barcode'));
    case 'taxRate':
      return firstValue(product.taxRate, specValue(product, 'tasa'));
    case 'unit':
      return firstValue(product.unitName, specValue(product, 'empaque'));
    case 'stock':
      return product.stock != null ? String(product.stock) : null;
    case 'inStock':
      if (product.inStock == null) return specValue(product, 'inStock');
      return product.inStock ? 'Sí' : 'No';
    default:
      return specValue(product, field);
  }
};

export const defaultCardFieldLabel = (field?: string): string | undefined => {
  if (!field || field === 'none') return undefined;
  if (field.startsWith('spec:')) return undefined;
  for (const group of CARD_FIELD_GROUPS) {
    const option = group.options.find((o) => o.value === field);
    if (option) return option.label;
  }
  return undefined;
};

export const buildCardFieldGroups = (
  attributes: Array<Pick<AttributeDefinition, 'key' | 'label' | 'visible'>> = [],
  selectedValues: string[] = []
): { id: string; label: string; options: CardFieldOption[] }[] => {
  const groups = CARD_FIELD_GROUPS.map((group) => ({
    id: group.id,
    label: group.label,
    options: group.options.map((option) => ({ value: option.value, label: option.label })),
  }));

  const known = new Set(groups.flatMap((group) => group.options.map((option) => option.value)));
  const redundantSpecKeys = new Set([
    'codigo',
    'tasa',
    'empaque',
    'familia',
    'serie',
    'uso',
    'category',
    'price',
    'name',
  ]);
  const configured = attributes
    .filter((attr) => attr.visible !== false && attr.key?.trim())
    .map((attr) => {
      const key = attr.key.trim().toLowerCase();
      return {
        key,
        value: `spec:${key}`,
        label: attr.label?.trim() || attr.key,
      };
    })
    .filter((option) => {
      if (known.has(option.key) || known.has(option.value) || redundantSpecKeys.has(option.key)) return false;
      known.add(option.value);
      return true;
    })
    .map(({ value, label }) => ({ value, label }));

  if (configured.length > 0) {
    groups.push({ id: 'configurados', label: 'Atributos configurados', options: configured });
  }

  const extras = selectedValues
    .filter((value) => value && value !== 'none' && !known.has(value))
    .map((value) => ({
      value,
      label:
        value === 'category'
          ? 'Categoría (ramo)'
          : value === 'brand'
            ? 'Marca (atributo)'
            : value.startsWith('spec:')
              ? `Spec: ${value.slice(5)}`
              : value,
    }));

  if (extras.length > 0) {
    groups.push({ id: 'guardados', label: 'Valores guardados', options: extras });
  }

  return groups;
};
